import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdEmail, MdLock } from "react-icons/md";
import { login } from "../../api/auth";
import { toast } from "react-hot-toast";
import { AxiosError } from "axios";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await login(email, password);
      toast.success("Logged in successfully!");
      navigate("/offers");
    } catch (err: unknown) {
      const error = err as AxiosError<{ detail?: string; message?: string }>;
      const msg = error.response?.data?.detail || error.response?.data?.message || "Login failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative">
      {/* Background half-and-half layer */}
      <div className="absolute inset-0 z-0 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:block bg-[#FF7A1A]"></div>
        <div className="bg-[#F3F4F6]"></div>
      </div>

      {/* Login card as second layer, centered over both halves */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] p-8 sm:p-10">
          <h1 className="text-2xl font-semibold text-[#606060] mb-7 text-center">
            Welcome back
          </h1>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-6">
            {/* Email */}
            <label className="block">
              <span className="block text-sm font-medium text-[#606060] mb-2">
                Email address
              </span>
              <div className="relative bg-[#EEF1F5] rounded-2xl overflow-hidden ring-1 ring-transparent focus-within:ring-orange-300 transition">
                <input
                  type="email"
                  required
                  className="w-full bg-transparent pl-4 pr-12 py-4 outline-none text-[#303030] placeholder:text-[#9aa3ae]"
                  placeholder="alex@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email address"
                  autoComplete="email"
                />
                <MdEmail
                  size={22}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3ae] pointer-events-none"
                />
              </div>
            </label>

            {/* Password */}
            <label className="block">
              <span className="block text-sm font-medium text-[#606060] mb-2">
                Password
              </span>
              <div className="relative bg-[#EEF1F5] rounded-2xl overflow-hidden ring-1 ring-transparent focus-within:ring-orange-300 transition">
                <input
                  type="password"
                  required
                  className="w-full bg-transparent pl-4 pr-12 py-4 outline-none text-[#303030] placeholder:text-[#9aa3ae]"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-label="Password"
                  autoComplete="current-password"
                />
                <MdLock
                  size={22}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3ae] pointer-events-none"
                />
              </div>
            </label>

            <div className="flex justify-end">
              <button
                type="button"
                className="text-[#0E2A6B] hover:underline text-sm font-medium"
                onClick={() => navigate("/forgot")}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FF7A1A] hover:bg-[#e56f16] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-2xl py-4 text-lg font-semibold shadow-md transition transform active:scale-[0.99]"
            >
              {submitting ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
